import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';

const WhatsAppManagerModal = ({ show, mode, manager, availableUsers, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    manager_type: 'basic',
    max_agents: 5,
    can_transfer_conversations: false,
    can_view_advanced_analytics: true,
    is_active: true
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && manager) {
      setFormData({
        user_id: manager.id || '',
        manager_type: manager.manager_type || 'basic',
        max_agents: manager.max_agents || 5,
        can_transfer_conversations: manager.can_transfer_conversations || false,
        can_view_advanced_analytics: manager.can_view_advanced_analytics !== false,
        is_active: manager.is_active !== false
      });
    } else if (mode === 'add') {
      setFormData({
        user_id: '',
        manager_type: 'basic',
        max_agents: 5,
        can_transfer_conversations: false,
        can_view_advanced_analytics: true,
        is_active: true
      });
    }
    setErrors({});
  }, [mode, manager, show]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Auto-adjust max_agents based on manager_type
    if (name === 'manager_type') {
      setFormData(prev => ({
        ...prev,
        manager_type: value,
        max_agents: value === 'upgraded' ? 999 : 5
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (mode === 'add' && !formData.user_id) {
      newErrors.user_id = 'Please select a user';
    }

    if (!formData.manager_type) {
      newErrors.manager_type = 'Please select a manager type';
    }

    if (formData.manager_type === 'basic' && (!formData.max_agents || formData.max_agents < 1)) {
      newErrors.max_agents = 'Max agents must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    if (mode === 'add') return 'Assign WhatsApp Manager';
    if (mode === 'edit') return 'Edit WhatsApp Manager';
    return 'WhatsApp Manager';
  };

  const getSelectedUserName = () => {
    if (mode === 'edit' && manager) {
      return `${manager.name} (${manager.email})`;
    }
    return '';
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{getModalTitle()}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {mode === 'add' && (
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Select User <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="select"
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleInputChange}
                    isInvalid={!!errors.user_id}
                  >
                    <option value="">Choose a manager user...</option>
                    {availableUsers?.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {errors.user_id}
                  </Form.Control.Feedback>
                  {availableUsers?.length === 0 && (
                    <Form.Text className="text-muted">
                      No available manager users found. Make sure users are active, have 'manager' role, and are not already assigned as WhatsApp managers.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
          )}

          {mode === 'edit' && (
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>User</Form.Label>
                  <Form.Control
                    type="text"
                    value={getSelectedUserName()}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Manager Type <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="select"
                  name="manager_type"
                  value={formData.manager_type}
                  onChange={handleInputChange}
                  isInvalid={!!errors.manager_type}
                >
                  <option value="basic">Basic (Limited Agents)</option>
                  <option value="upgraded">Upgraded (Unlimited Agents)</option>
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {errors.manager_type}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Max Agents</Form.Label>
                <Form.Control
                  type="number"
                  name="max_agents"
                  value={formData.max_agents}
                  onChange={handleInputChange}
                  disabled={formData.manager_type === 'upgraded'}
                  min="1"
                  max="100"
                  isInvalid={!!errors.max_agents}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.max_agents}
                </Form.Control.Feedback>
                {formData.manager_type === 'upgraded' && (
                  <Form.Text className="text-muted">
                    Unlimited agents for upgraded managers
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Permissions</Form.Label>
                <div className="mt-2">
                  <Form.Check
                    type="checkbox"
                    name="can_transfer_conversations"
                    label="Can transfer conversations between agents"
                    checked={formData.can_transfer_conversations}
                    onChange={handleInputChange}
                  />
                  <Form.Check
                    type="checkbox"
                    name="can_view_advanced_analytics"
                    label="Can view advanced analytics"
                    checked={formData.can_view_advanced_analytics}
                    onChange={handleInputChange}
                  />
                  <Form.Check
                    type="checkbox"
                    name="is_active"
                    label="Active manager profile"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Saving...' : (mode === 'add' ? 'Assign Manager' : 'Update Manager')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default WhatsAppManagerModal;
